import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Mail, Phone, Home, FileText } from 'lucide-react';
import { BASEURL } from '../constants';

const StudentChangeRequests = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [expandedRequest, setExpandedRequest] = useState(null); // Track which request is expanded

  const getChangeRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        BASEURL + "Student/StudentChangeRequests/" + localStorage.getItem("studentId")  
      );
      setRequests(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getChangeRequests();
  }, []);

  const toggleAccordion = (requestId) => {
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 0:
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 1:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 2:
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 3:
        return <XCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return 'bg-yellow-50 border-yellow-200';
      case 1:
        return 'bg-green-50 border-green-200';
      case 2:
        return 'bg-red-50 border-red-200';
      case 3:
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 0:
        return 'bg-yellow-100 text-yellow-800';
      case 1:
        return 'bg-green-100 text-green-800';
      case 2:
        return 'bg-red-100 text-red-800';
      case 3:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper functions to check if sections have data
  const hasContactInfo = (data) => {
    return data?.email || data?.mobile;
  };

  const hasResidentialAddress = (data) => {
    return data?.localAddressLine1 || data?.localAddressLine2 || data?.localAddressLine3 || 
           data?.lSuburb || data?.lState || data?.lPostCode || data?.lCountry;
  };

  const hasMailingAddress = (data) => {
    return data?.mailingAddressLine1 || data?.mailingAddressLine2 || data?.mailingAddressLine3 || 
           data?.mSuburb || data?.mState || data?.mPostCode || data?.mCountry;
  };

  const hasAvetmissAddress = (data) => {
    return data?.propertyName || data?.flatDetail || data?.streetNo || data?.streetName || 
           data?.avetmissSuburb || data?.avetmissState || data?.avetmissPostCode || data?.avetmissCountry;
  };

  // Render request data details
  const renderRequestData = (data) => {
    if (!data) return null;

    const hasAnyData = hasContactInfo(data) || hasResidentialAddress(data) || 
                       hasMailingAddress(data) || hasAvetmissAddress(data);

    if (!hasAnyData) {
      return (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center py-4">No additional information available</p>
        </div>
      );
    }

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
        {/* Contact Information */}
        {hasContactInfo(data) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-gray-900">Contact Information</h4>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.email && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm text-gray-900">{data.email}</p>
                  </div>
                )}
                {data.mobile && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Mobile</p>
                    <p className="text-sm text-gray-900">{data.mobile}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Local Address */}
        {hasResidentialAddress(data) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-gray-900">Local Address</h4>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                {data.localAddressLine1 && (
                  <p className="text-sm text-gray-900">{data.localAddressLine1}</p>
                )}
                {data.localAddressLine2 && (
                  <p className="text-sm text-gray-900">{data.localAddressLine2}</p>
                )}
                {data.localAddressLine3 && (
                  <p className="text-sm text-gray-900">{data.localAddressLine3}</p>
                )}
                {(data.lSuburb || data.lState || data.lPostCode || data.lCountry) && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                    {data.lSuburb && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Suburb</p>
                        <p className="text-sm text-gray-900">{data.lSuburb}</p>
                      </div>
                    )}
                    {data.lState && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">State</p>
                        <p className="text-sm text-gray-900">{data.lState}</p>
                      </div>
                    )}
                    {data.lPostCode && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Postcode</p>
                        <p className="text-sm text-gray-900">{data.lPostCode}</p>
                      </div>
                    )}
                    {data.lCountry && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Country</p>
                        <p className="text-sm text-gray-900">{data.lCountry}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mailing Address */}
        {hasMailingAddress(data) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-gray-900">Mailing Address</h4>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                {data.mailingAddressLine1 && (
                  <p className="text-sm text-gray-900">{data.mailingAddressLine1}</p>
                )}
                {data.mailingAddressLine2 && (
                  <p className="text-sm text-gray-900">{data.mailingAddressLine2}</p>
                )}
                {data.mailingAddressLine3 && (
                  <p className="text-sm text-gray-900">{data.mailingAddressLine3}</p>
                )}
                {(data.mSuburb || data.mState || data.mPostCode || data.mCountry) && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                    {data.mSuburb && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Suburb</p>
                        <p className="text-sm text-gray-900">{data.mSuburb}</p>
                      </div>
                    )}
                    {data.mState && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">State</p>
                        <p className="text-sm text-gray-900">{data.mState}</p>
                      </div>
                    )}
                    {data.mPostCode && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Postcode</p>
                        <p className="text-sm text-gray-900">{data.mPostCode}</p>
                      </div>
                    )}
                    {data.mCountry && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Country</p>
                        <p className="text-sm text-gray-900">{data.mCountry}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AVETMISS Address */}
        {hasAvetmissAddress(data) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-gray-900">AVETMISS Address</h4>
              </div>
            </div>
            <div className="p-4">
              {(data.propertyName || data.flatDetail || data.streetNo || data.streetName) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  {data.propertyName && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Property Name</p>
                      <p className="text-sm text-gray-900">{data.propertyName}</p>
                    </div>
                  )}
                  {data.flatDetail && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Flat/Unit Detail</p>
                      <p className="text-sm text-gray-900">{data.flatDetail}</p>
                    </div>
                  )}
                  {data.streetNo && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Street Number</p>
                      <p className="text-sm text-gray-900">{data.streetNo}</p>
                    </div>
                  )}
                  {data.streetName && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Street Name</p>
                      <p className="text-sm text-gray-900">{data.streetName}</p>
                    </div>
                  )}
                </div>
              )}
              {(data.avetmissSuburb || data.avetmissState || data.avetmissPostCode || data.avetmissCountry) && (
                <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 ${(data.propertyName || data.flatDetail || data.streetNo || data.streetName) ? 'border-t border-gray-100' : ''}`}>
                  {data.avetmissSuburb && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Suburb</p>
                      <p className="text-sm text-gray-900">{data.avetmissSuburb}</p>
                    </div>
                  )}
                  {data.avetmissState && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">State</p>
                      <p className="text-sm text-gray-900">{data.avetmissState}</p>
                    </div>
                  )}
                  {data.avetmissPostCode && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Postcode</p>
                      <p className="text-sm text-gray-900">{data.avetmissPostCode}</p>
                    </div>
                  )}
                  {data.avetmissCountry && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Country</p>
                      <p className="text-sm text-gray-900">{data.avetmissCountry}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-24 bg-gray-200 rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No change requests found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Change Requests
        </h2>

        <div className="space-y-4">
          {requests.map((request) => {
            const isExpanded = expandedRequest === request.requestId;

            return (
              <div
                key={request.requestId}
                className={`rounded-lg border transition-all ${getStatusColor(request.status)}`}
              >
                {/* Accordion Header */}
                <div
                  className="p-5 cursor-pointer hover:bg-opacity-80 transition-colors"
                  onClick={() => toggleAccordion(request.requestId)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Icon & Content */}
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0">
                        {getStatusIcon(request.status)}
                      </div>

                      <div className="flex-1 space-y-2 min-w-0">
                        {/* Status Badge & Date */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                              request.status
                            )}`}
                          >
                            {request.statusName}
                          </span>
                          <span className="text-sm text-gray-500">
                            {request.requestDate}
                          </span>
                        </div>

                        {/* Message */}
                        <p className="text-gray-700 leading-relaxed break-words">
                          {request.statusMessage}
                        </p>
                      </div>
                    </div>

                    {/* Right Side Icons */}
                    <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
                      {/* Processed Badge */}
                      {request.isProcessed && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-green-700 border border-green-200">
                          <CheckCircle className="w-3 h-3" />
                          Processed
                        </span>
                      )}

                      {/* Expand/Collapse Icon */}
                      <div className="ml-2">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-5">
                    {renderRequestData(request.requestData)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentChangeRequests;